import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PressableScale } from "pressto";
import { Stack } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useCameras } from "@src/services/api/hooks";
import { getCameraDisplayName, getCameraKeyString, getSiteString } from "@src/utils/key-parser";
import { ActivityList } from "@src/features/activity/components/ActivityList";
import { CameraFilterBottomSheet } from "@src/features/cameras/components/CameraFilterBottomSheet";

export default function ActivityScreen() {
  return <MobileActivityScreen />;
}

function MobileActivityScreen() {
  const { theme } = useUnistyles();

  const { data: cameras, isLoading: isCamerasLoading } = useCameras();

  const [selectedCameraKey, setSelectedCameraKey] = useState<string | null>(null);
  const bottomSheetRef = useRef<BottomSheetModal | null>(null);

  // Initialise selection to "all cameras" for the site
  useEffect(() => {
    if (cameras && cameras.length > 0 && selectedCameraKey === null) {
      // Leave as null to indicate "All cameras" for this site
      setSelectedCameraKey(null);
    }
  }, [cameras, selectedCameraKey]);

  const selectedCamera = useMemo(() => {
    if (!cameras || !selectedCameraKey) return null;
    return cameras.find((c) => getCameraKeyString(c) === selectedCameraKey) || null;
  }, [cameras, selectedCameraKey]);

  const cameraLocation = useMemo(() => {
    if (!selectedCamera) return "";
    return getSiteString(selectedCamera);
  }, [selectedCamera]);

  const cameraDisplayName = useMemo(() => {
    if (!selectedCameraKey) return "All cameras";
    return getCameraDisplayName(selectedCameraKey) || selectedCameraKey;
  }, [selectedCameraKey]);

  const headerTitle = useCallback(
    () => (
      <View flexDirection="row" alignItems="center" gap="xs">
        <Text textStyle="md/medium">{cameraLocation || "Activity"}</Text>
        {cameraDisplayName && (
          <Text textStyle="md/medium" color="textMuted">
            {cameraDisplayName}
          </Text>
        )}
      </View>
    ),
    [cameraLocation, cameraDisplayName],
  );

  const headerRight = useCallback(
    () => (
      <PressableScale
        onPress={() => {
          bottomSheetRef.current?.present();
        }}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        style={{
          paddingHorizontal: 16,
          paddingVertical: 4,
        }}
      >
        <Icon name="settingsOutline" size={24} color={theme.colors.icon} />
      </PressableScale>
    ),
    [theme.colors.icon],
  );

  const headerOptions = useMemo(() => {
    const baseOptions = {
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
      headerTintColor: theme.colors.text,
      headerBackButtonDisplayMode: "minimal" as const,
    };

    if (isCamerasLoading || !cameras || cameras.length === 0) {
      return {
        ...baseOptions,
        title: "Activity",
      };
    }

    return {
      ...baseOptions,
      headerTitle,
      headerRight,
      headerShadowVisible: false,
    };
  }, [theme.colors.background, theme.colors.text, isCamerasLoading, cameras, headerTitle, headerRight]);

  const handleSelectCamera = (cameraKey: string | null) => {
    setSelectedCameraKey(cameraKey);
    bottomSheetRef.current?.close();
  };

  return (
    <>
      <Stack.Screen options={headerOptions} />

      <ActivityList cameraKey={selectedCameraKey} />

      {cameras && (
        <CameraFilterBottomSheet
          bottomSheetRef={bottomSheetRef}
          cameras={cameras}
          selectedCameraKey={selectedCameraKey}
          onSelectCamera={handleSelectCamera}
          showAllOption
          title="Select Camera"
          subtitle="Choose a camera to filter the activity feed"
        />
      )}
    </>
  );
}
